import { useEffect, useState } from 'react';
import type { FacilityRecord, MarketRecord, PagedResult } from '../types/index';
import type { FieldSortCriterionValue } from '../utils/tableSort';
import { getFacilitiesTotals } from './useFacilities';
import { getMarkets } from './useMarkets';

export default function useFacilitiesPageResults({ organizationId, page, pageSize, filters, sort }: {
  organizationId: number | string | null;
  page: number;
  pageSize: number;
  filters: { name: string; marketId: string };
  sort: FieldSortCriterionValue;
}) {
  const [state, setState] = useState<{
    organizationId: number | string | null;
    results: PagedResult<FacilityRecord>;
    markets: MarketRecord[];
  }>({ organizationId, results: { page, pageSize, total: 0, data: [] }, markets: [] });

  useEffect(() => {
    let current = true;
    setState({ organizationId, results: { page, pageSize, total: 0, data: [] }, markets: [] });
    if (!organizationId) return () => { current = false; };

    getMarkets(organizationId)
      .then((markets) => { if (current) setState((value) => ({ ...value, markets })); })
      .catch((error) => { if (current) console.log(error); });

    return () => { current = false; };
  }, [organizationId]);

  useEffect(() => {
    let current = true;
    if (!organizationId) return () => { current = false; };

    const payload = {
      organizationId,
      page,
      pageSize,
      sortField: sort.fieldName,
      sortAscending: sort.isAscending,
      ...(filters.marketId === 'all' ? {} : { marketId: filters.marketId }),
      ...(filters.name ? { name: filters.name } : {}),
    };
    getFacilitiesTotals(payload)
      .then((results) => {
        if (current) setState((value) => ({ ...value, results: { ...results, page, pageSize } }));
      })
      .catch((error) => { if (current) console.log(error); });

    return () => { current = false; };
  }, [filters, organizationId, page, pageSize, sort.fieldName, sort.isAscending]);

  return state.organizationId === organizationId
    ? { results: state.results, markets: state.markets }
    : { results: { page: 0, pageSize, total: 0, data: [] }, markets: [] };
}
